import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Check, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const URGENCIES = ["Immediate (< 2 weeks)", "This month", "This quarter", "Exploring options"];
const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Contract-to-hire"];

export default function RecruitingInquiry() {
    const [form, setForm] = useState({
        employer_name: "", contact_name: "", work_email: "", phone: "",
        role_title: "", location: "", salary_range: "",
        employment_type: "Full-time", hiring_urgency: "", num_hires: 1,
        hard_to_fill_reason: "", message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const submit = async (e) => {
        e.preventDefault();
        if (!form.employer_name || !form.contact_name || !form.work_email || !form.role_title) {
            toast.error("Company, contact name, email and role title are required.");
            return;
        }
        try {
            setSubmitting(true);
            await api.post("/leads/recruiting", {
                ...form,
                num_hires: Number(form.num_hires) || 1,
            });
            setSent(true);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6 py-16">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg text-center card-soft p-10">
                    <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center mb-4"><Check size={24} /></div>
                    <h1 className="font-heading font-semibold text-2xl text-foreground">Got it — we'll reach out.</h1>
                    <p className="mt-2 text-sm text-muted-foreground">A Simonara recruiter will contact you within one business day to scope the engagement.</p>
                    <Link to="/" className="btn-outline mt-6 inline-flex text-sm">Back home</Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-6 py-12 md:py-20">
            <div className="mb-8">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-600 font-semibold">
                    <Sparkles size={13} /> Simonara Recruit™
                </div>
                <h1 className="mt-2 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                    Get recruiting help
                </h1>
                <p className="mt-3 text-sm text-muted-foreground max-w-lg">
                    Hard-to-fill role? Rapid-hire quarter? Our recruiters run the search on top of Simonara's verified talent pool. Tell us about the role and we'll come back with a scope + fee within one business day.
                </p>
            </div>
            <form onSubmit={submit} data-testid="recruiting-form" className="space-y-4 card-soft p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-xs font-medium text-foreground">Company *</label>
                        <Input required data-testid="rec-company" value={form.employer_name} onChange={update("employer_name")} className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Your name *</label>
                        <Input required data-testid="rec-name" value={form.contact_name} onChange={update("contact_name")} className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Work email *</label>
                        <Input required type="email" data-testid="rec-email" value={form.work_email} onChange={update("work_email")} className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Phone (optional)</label>
                        <Input data-testid="rec-phone" value={form.phone} onChange={update("phone")} className="mt-1" /></div>
                    <div className="md:col-span-2"><label className="text-xs font-medium text-foreground">Role title *</label>
                        <Input required data-testid="rec-role" value={form.role_title} onChange={update("role_title")} placeholder="e.g. Senior Staff Engineer, Head of Product" className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Location</label>
                        <Input data-testid="rec-location" value={form.location} onChange={update("location")} placeholder="Remote, NYC, hybrid…" className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Salary range</label>
                        <Input data-testid="rec-salary" value={form.salary_range} onChange={update("salary_range")} placeholder="$140k-180k" className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Employment type</label>
                        <select value={form.employment_type} onChange={update("employment_type")} data-testid="rec-emptype" className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm">
                            {EMPLOYMENT_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select></div>
                    <div><label className="text-xs font-medium text-foreground">Urgency</label>
                        <select value={form.hiring_urgency} onChange={update("hiring_urgency")} data-testid="rec-urgency" className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm">
                            <option value="">Select…</option>{URGENCIES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select></div>
                    <div><label className="text-xs font-medium text-foreground">Number of hires</label>
                        <Input type="number" min="1" max="500" data-testid="rec-hires" value={form.num_hires} onChange={update("num_hires")} className="mt-1" /></div>
                </div>
                <div><label className="text-xs font-medium text-foreground">Why has this been hard to fill? (optional)</label>
                    <Textarea data-testid="rec-hardtofill" value={form.hard_to_fill_reason} onChange={update("hard_to_fill_reason")} rows={2} className="mt-1" placeholder="Niche skill set, geographic constraints, salary expectations…" /></div>
                <div><label className="text-xs font-medium text-foreground">Anything else we should know?</label>
                    <Textarea data-testid="rec-message" value={form.message} onChange={update("message")} rows={3} className="mt-1" /></div>
                <button type="submit" disabled={submitting} data-testid="rec-submit" className="btn-green text-sm inline-flex items-center disabled:opacity-60">
                    {submitting ? <><Loader2 size={13} className="animate-spin mr-2" />Sending…</> : <>Request recruiting help <ArrowRight size={13} className="ml-2" /></>}
                </button>
            </form>
        </div>
    );
}
